/**
 * Small pure helpers the detail screens share: dates, ages, the preview type of a
 * signed URL, and the way back to the list an admin came from.
 */

/** ISO timestamp or date → `YYYY-MM-DD` (Gregorian). `null` for nothing to show. */
export function isoDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toISOString().slice(0, 10);
}

/**
 * A local calendar date as `YYYY-MM-DD`. Built from the local parts rather than
 * `toISOString`, which would render midnight in Riyadh (UTC+3) as the day before in UTC
 * and shift a date window a day early.
 */
export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Whole years since a `YYYY-MM-DD` date of birth.
 *
 * Read from the date parts rather than from millisecond arithmetic: a DOB is a calendar
 * date with no time zone, and dividing by a year of milliseconds is off by one around a
 * birthday, depending on where the browser is.
 */
export function ageFromDateOfBirth(
  dob: string | null | undefined,
  now = new Date(),
): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dob ?? '');
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  let age = now.getFullYear() - year;
  const beforeBirthday =
    now.getMonth() + 1 < month || (now.getMonth() + 1 === month && now.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

export type PreviewKind = 'image' | 'pdf' | 'other';

/**
 * How to embed a preview URL. The queue sends no content type, so the extension in the
 * path decides; anything unrecognized goes in a frame, which the browser renders or
 * offers to download, and the open-in-a-new-tab link is always there beside it.
 */
export function previewKind(url: string): PreviewKind {
  const path = url.split(/[?#]/)[0]!.toLowerCase();
  if (/\.(png|jpe?g|gif|webp|avif|bmp|svg)$/.test(path)) return 'image';
  if (path.endsWith('.pdf')) return 'pdf';
  return 'other';
}

/** What a list puts on `location.state` when it opens a detail screen. */
export interface ListReturnState {
  from: string;
}

/**
 * The list URL the back link returns to — the one the admin came from, filters and all,
 * when the router state says so and it really is that list; the bare list otherwise
 * (a deep link, a refresh in a new tab).
 */
export function listReturnPath(state: unknown, listPath: string): string {
  const from = (state as Partial<ListReturnState> | null)?.from;
  if (typeof from !== 'string') return listPath;
  const path = from.split('?')[0];
  return path === listPath ? from : listPath;
}

/**
 * Whether a click on a clickable card or row landed on something with its own action —
 * a link, a button, a form field, a media player's controls, or a region marked
 * `data-keep-click` such as an inline confirmation — which then keeps the click to itself.
 */
export function clickedInteractive(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('a,button,input,textarea,select,label,video,audio,[data-keep-click]') !== null
  );
}
